/**
 * Types, constants, and pure helpers for the warmup settings form. These live
 * apart from `WarmupSettingsForm.tsx` so that file can export only React
 * components — a module that mixes non-component exports with components is
 * disqualified from Vite Fast Refresh and forces a full page reload on edit.
 */

import type { CSSProperties } from 'react';

import { ErrorCode } from '@trulyinbox/errors';
import {
  TARGET_INDUSTRY_VALUES,
  SUBTOPIC_VALUES,
  WARMUP_MIN_LIMIT,
  REPLY_RATE_ABSOLUTE_CAP,
} from '@trulyinbox/types';

import type { ContentMode } from '@/lib/email-accounts';
import { PROVIDER_BRAND_COLOR } from '@/icons/ProviderIcon';
import type { SplitRule } from './warmup-schedule-split';

export type { ContentMode };

// ── Types ────────────────────────────────────────────────────────────────────

export type ScheduleDraft = {
  dayOfWeek: number;
  /** 0..1, contiguous from 0 per day, monotonic with `startTime`. */
  slotIndex: number;
  /** 1..100 integer; an enabled day's slot weights sum to exactly 100. */
  weight: number;
  enabled: boolean;
  startTime: string;
  endTime: string;
};

export type WarmupDraft = {
  senderName: string;
  warmupFolder: string;
  /** The user's max daily sends ({@link WARMUP_MIN_LIMIT}–plan cap), as a
   * string: the number input hands back the raw `e.target.value`, which is
   * blank mid-edit. Validated by {@link validateWarmupMaxLimit}. */
  warmupMaxLimit: string;
  timezone: string;
  language: string;
  contentMode: ContentMode;
  industry: string;
  topics: string[];
  customPrompt: string;
  espMode: 'AUTO' | 'CUSTOM';
  espGooglePct: number;
  espMicrosoftPct: number;
  espOthersPct: number;
  // ── Per-section plan gates (mirrored from the settings response) ───────────
  // Six of them now — `replyRateAllowed` joined in Part B.
  // `false` renders the section visible-but-disabled with a `PlanGateNotice`,
  // and makes `handleSave` OMIT that section's fields (the API gates on field
  // presence, so re-posting an unchanged locked value 403s the whole save).
  customEspAllowed: boolean;
  customContentAllowed: boolean;
  templatesAllowed: boolean;
  customScheduleAllowed: boolean;
  customLanguageAllowed: boolean;
  replyRateAllowed: boolean;
  planName: string | null;
  dkimSelector: string;
  schedule: ScheduleDraft[];
  /** Assigned template collection id, or `null` for 100% AI content (default). */
  templateCollectionId: number | null;
  /** Per-send chance (%, 0-100) of drawing from `templateCollectionId` instead
   * of AI-generated content. Meaningless while `templateCollectionId` is null. */
  templateRatioPercent: number;
};

/**
 * `WarmupDraft` keys the shared form can render as "not chosen yet" instead of
 * as the draft's real value — only bulk sets this, via `unselectedWarmupFields`.
 * A Select shows its placeholder; the max-limit input renders blank with a
 * placeholder saying each mailbox keeps its own max. Kept off `WarmupDraft`'s
 * own field types so code reading the real value never sees a blank sentinel.
 */
export type UnselectedWarmupField =
  | 'warmupMaxLimit'
  | 'language'
  | 'contentMode'
  | 'espMode'
  | 'templateCollectionId';

// ── Constants ─────────────────────────────────────────────────────────────────

export const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
/** Full weekday names, same Mon-first order as {@link DAY_LABELS} — what the
 * schedule rows and their accessible names read. */
export const DAY_FULL_LABELS = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
];

// ── Plan gating ───────────────────────────────────────────────────────────────

/**
 * Toast copy for each per-section plan-gate denial, keyed by the 6-digit
 * `errorCode` the API throws. The backend mints ONE code per gated section
 * precisely so the client can name the right one — branch on the code, never on
 * the message string.
 *
 * The UI disables every gated control, so these only fire when a stale page
 * posts a value the plan no longer grants (e.g. the tab was open across a
 * downgrade). Shared by the single-account save (`use-warmup-settings-form.ts`)
 * and the bulk enqueue (`BulkWarmupSettingsPage.tsx`) — both write paths reject
 * the same patches, so they must render the same prose.
 *
 * These name no tier: a toast fired from a save handler has no plan catalog to
 * resolve against, and a hardcoded "Growth+" is wrong the moment the catalog
 * moves. The section's `<LockedFeature>` card is
 * where the unlocking plan is named.
 */
export const WARMUP_PLAN_GATE_MESSAGES: Readonly<Partial<Record<string, string>>> = {
  [ErrorCode.EmailAccounts.UpdateWarmupSettings.CustomEspRequiresUpgrade]:
    "Custom ESP distribution isn't available on your current plan.",
  [ErrorCode.EmailAccounts.UpdateWarmupSettings.CustomContentRequiresUpgrade]:
    "Industry & Topic content isn't available on your current plan.",
  [ErrorCode.EmailAccounts.UpdateWarmupSettings.TemplatesRequireUpgrade]:
    "Assigning a template collection isn't available on your current plan.",
  [ErrorCode.EmailAccounts.UpdateWarmupSettings.CustomScheduleRequiresUpgrade]:
    "Editing the sending schedule isn't available on your current plan.",
  [ErrorCode.EmailAccounts.UpdateWarmupSettings.CustomLanguageRequiresUpgrade]:
    "Choosing the warmup content language isn't available on your current plan.",
  // 022313 — the reply-rate SECTION is locked. Deliberately absent from this map
  // is 022307 (ReplyRateExceedsPlanLimit), which is the numeric ceiling on a plan
  // that DOES hold the section: it is a validation failure, not a paywall, and
  // routing it here would send a paying customer to the upgrade prompt for a
  // value they only need to lower.
  [ErrorCode.EmailAccounts.UpdateWarmupSettings.ReplyRateRequiresUpgrade]:
    "Setting a warmup reply rate isn't available on your current plan.",
};

// ── Daily sending limit ───────────────────────────────────────────────────────

/**
 * Clamp a max daily limit into the settable range: at least
 * {@link WARMUP_MIN_LIMIT}, at most the plan cap (`null` = no ceiling). The
 * single-account seed runs the stored value through this so the input shows —
 * and a save sends — what the server enforces anyway (a plan change lowers an
 * over-cap max to the cap).
 */
export function clampWarmupMaxLimit(value: number, cap: number | null): number {
  const floored = Math.max(WARMUP_MIN_LIMIT, Number.isFinite(value) ? value : WARMUP_MIN_LIMIT);
  return cap == null ? floored : Math.min(floored, Math.max(cap, WARMUP_MIN_LIMIT));
}

/**
 * Client-side mirror of the server's max-limit rules: a whole number, at least
 * {@link WARMUP_MIN_LIMIT} (the DTO's `@Min`), at most the plan cap (above it
 * the service throws `022303`). A `null` cap is unlimited, so only the floor
 * applies. Returns the user-facing message, or `null` when valid.
 */
export function validateWarmupMaxLimit(value: string, cap: number | null): string | null {
  const n = Number(value);
  const valid = Number.isInteger(n) && n >= WARMUP_MIN_LIMIT && (cap == null || n <= cap);
  if (valid) return null;
  return cap == null
    ? `Max emails per day must be a whole number of at least ${WARMUP_MIN_LIMIT}.`
    : `Max emails per day must be between ${WARMUP_MIN_LIMIT} and ${cap} on your plan.`;
}

// ── Slots ──────────────────────────────────────────────────────────────────────

/** Max slots per day — mirrors the backend `ScheduleSlotsConstraint` ceiling. */
export const MAX_SLOTS_PER_DAY = 2;
/**
 * Minimum cumulative sending window (minutes) an ENABLED day must cover — the
 * sum of (end − start) across that day's enabled slots must be ≥ this. Mirrors
 * the backend 6h rule (`InsufficientDailyWindow` / `022306`). Exactly 6h passes;
 * < 6h is rejected.
 */
export const MIN_DAILY_WINDOW_MIN = 360;
/**
 * Minimum duration (minutes) a single ENABLED-day slot must span — each slot's
 * `(end − start)` must be ≥ this. Mirrors the server's per-slot send-window floor
 * (`validateSendWindow` / `MIN_SEND_WINDOW_HOURS`, also enforced by the DTO's
 * `ScheduleSlotsConstraint`), which throws `WindowTooShort` / `022302`. Exactly
 * 2h passes; < 2h is rejected. Distinct from {@link PRESET_SLOT_MIN_WIDTH_MIN} (30),
 * which is only the minimum half-width `splitWindowInTwo` will emit.
 */
export const MIN_SLOT_DURATION_MIN = 120;
/** An enabled day's slot weights must sum to exactly this (mirrors the backend). */
export const SLOT_WEIGHT_SUM = 100;
/** Minimum per-slot weight (backend floors at 1). */
export const SLOT_WEIGHT_MIN = 1;
// ── Two-slot split geometry ─────────────────────────────────────────────────────
// Constants behind `splitWindowInTwo`, which `addSlot` uses to turn a day's one
// window into two. They mirror the backend gap rule.

/** Default gap (minutes) `splitWindowInTwo` opens between the two slots. */
export const DISTRIBUTION_SLOT_GAP_MIN = 120;
/**
 * Validation floor — a slot must start STRICTLY MORE than this many minutes
 * after the previous slot ends. Same number as the backend `SLOT_MIN_GAP_MIN`.
 */
export const SLOT_MIN_GAP_MIN = 30;
/** Grid the split boundaries snap to (minutes). */
export const SPLIT_ROUND_MIN = 30;
/** A slot emitted by `splitWindowInTwo` is never narrower than this many minutes. */
export const PRESET_SLOT_MIN_WIDTH_MIN = 30;

/**
 * A 7-day schedule with Mon–Fri (indices 0–4 per `DAY_LABELS`) enabled on the
 * given window and Sat/Sun disabled. Each day is a SINGLE slot
 * (`slotIndex 0, weight 100`). Backs `defaultSchedule` and so "Reset schedule to
 * default".
 */
export function presetSchedule(start: string, end: string): ScheduleDraft[] {
  return Array.from({ length: 7 }, (_, i) => ({
    dayOfWeek: i,
    slotIndex: 0,
    weight: SLOT_WEIGHT_SUM,
    enabled: i < 5,
    startTime: start,
    endTime: end,
  }));
}

/** Business Hours is the product default (matches `seedDraft`'s seed defaults). */
export function defaultSchedule(): ScheduleDraft[] {
  return presetSchedule('09:00', '17:00');
}

/**
 * Value-equality over two schedules (both sides are built by the same
 * `dayOfWeek`-ascending / `slotIndex`-ascending construction throughout this
 * module, so a positional comparison is safe — no sort needed). Backs the
 * single-account "Changed" badge (draft vs. the loaded value) and the bulk
 * page's Reset-to-Default guard (skip touching the field when the reset
 * value is identical to what's already on screen, so a click that changes
 * nothing visible doesn't silently arm a multi-account overwrite).
 */
export function scheduleEquals(a: ScheduleDraft[], b: ScheduleDraft[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((slot, i) => {
    const other = b[i];
    return (
      slot.dayOfWeek === other.dayOfWeek &&
      slot.slotIndex === other.slotIndex &&
      slot.weight === other.weight &&
      slot.enabled === other.enabled &&
      slot.startTime === other.startTime &&
      slot.endTime === other.endTime
    );
  });
}

// ── Day mode (Weekdays / Everyday) ─────────────────────────────────────────────

export type DayMode = 'WEEKDAYS' | 'EVERYDAY' | 'CUSTOM';

// `CUSTOM` is never *applied* — it's the derived state when the enabled pattern
// is neither all-7 nor exactly Mon–Fri (see `detectDayMode`).
export const DAY_MODES = [
  { key: 'WEEKDAYS', label: 'Weekdays' },
  { key: 'EVERYDAY', label: 'Everyday' },
] as const;

/**
 * Flip the `enabled` flags across all 7 rows to match the chosen mode, leaving
 * each row's start/end times intact. WEEKDAYS = Mon–Fri (indices 0–4) on,
 * weekend off; EVERYDAY = all 7 on. CUSTOM is a no-op (derived only).
 */
export function applyDayMode(schedule: ScheduleDraft[], mode: DayMode): ScheduleDraft[] {
  if (mode === 'CUSTOM') return schedule;
  return schedule.map((r) => ({
    ...r,
    enabled: mode === 'EVERYDAY' ? true : r.dayOfWeek < 5,
  }));
}

/**
 * Which day mode the current schedule matches — drives the active-button
 * highlight. All 7 enabled → EVERYDAY; exactly Mon–Fri-on / weekend-off →
 * WEEKDAYS; anything else (including all-off) → CUSTOM.
 */
export function detectDayMode(schedule: ScheduleDraft[]): DayMode {
  if (schedule.every((r) => r.enabled)) return 'EVERYDAY';
  if (schedule.every((r) => r.enabled === r.dayOfWeek < 5)) return 'WEEKDAYS';
  return 'CUSTOM';
}

// ── Group / ungroup by day ──────────────────────────────────────────────────────

/**
 * Group a flat slot list into a 7-element array (Mon..Sun), each holding that
 * day's slot rows ordered by `slotIndex`. Days are always present (0..6); a day
 * with no rows yields `[]`. The flat `ScheduleDraft[]` is the canonical request
 * shape, so this is the read-side projection the UI groups on.
 */
export function groupByDay(schedule: ScheduleDraft[]): ScheduleDraft[][] {
  const byDay: ScheduleDraft[][] = Array.from({ length: 7 }, () => []);
  for (const row of schedule) {
    if (row.dayOfWeek >= 0 && row.dayOfWeek < 7) byDay[row.dayOfWeek].push(row);
  }
  return byDay.map((rows) => [...rows].sort((a, b) => a.slotIndex - b.slotIndex));
}

/**
 * Flatten a grouped (day → slot rows) structure back into the canonical flat
 * `ScheduleDraft[]`, ordered by `(dayOfWeek, slotIndex)`. Inverse of
 * {@link groupByDay}.
 */
export function ungroupByDay(byDay: ScheduleDraft[][]): ScheduleDraft[] {
  return byDay
    .flatMap((rows) => [...rows].sort((a, b) => a.slotIndex - b.slotIndex))
    .sort((a, b) => a.dayOfWeek - b.dayOfWeek || a.slotIndex - b.slotIndex);
}

/**
 * Sum of an enabled day's slot weights — drives the live "sum = 100" indicator
 * and the {@link validateSchedule} weight check.
 */
export function daySlotWeightSum(slots: ScheduleDraft[]): number {
  return slots.reduce((acc, s) => acc + (Number.isFinite(s.weight) ? s.weight : 0), 0);
}

/**
 * Cumulative sending-window minutes for a day — Σ(end − start) across its slots.
 * Only well-formed slots (`start < end`) contribute; an inverted/degenerate slot
 * adds 0. Backs the live ≥6h indicator and the {@link validateSchedule} window
 * check (mirrors the backend's cumulative-window rule).
 */
export function daySlotMinutes(slots: ScheduleDraft[]): number {
  return slots.reduce((acc, s) => {
    const start = toMinutes(s.startTime);
    const end = toMinutes(s.endTime);
    return acc + (Number.isFinite(start) && Number.isFinite(end) && end > start ? end - start : 0);
  }, 0);
}

/**
 * Whether a day has any slot shorter than the per-slot 2h floor
 * ({@link MIN_SLOT_DURATION_MIN}) — mirrors the backend `WindowTooShort` /
 * `022302` per-slot rule. Only well-formed slots (`start < end`) are measured.
 * The caller gates this on the day being enabled (disabled days are exempt, as
 * the backend's `if (enabled)` gate does). Backs the inline indicator; the
 * {@link validateSchedule} per-slot duration check shares the same floor.
 */
export function dayHasShortSlot(slots: ScheduleDraft[]): boolean {
  return slots.some((s) => {
    const start = toMinutes(s.startTime);
    const end = toMinutes(s.endTime);
    return Number.isFinite(start) && Number.isFinite(end) && end > start && end - start < MIN_SLOT_DURATION_MIN;
  });
}

// ── Weight auto-rebalance (pure; operate on ONE day's slot rows) ─────────────────
// A slot's weight is set by the schedule's split rule or, under "by my own %",
// typed per slot — and every mutation re-normalises the day to an exact sum of
// 100. These helpers are that normaliser;
// they preserve each row's window/index/enabled and touch only `weight`.

function clampNum(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Round every slot weight to an integer while preserving an exact sum of
 * {@link SLOT_WEIGHT_SUM}. The ±1 rounding residue is absorbed by the first
 * other slot that can take it within [0, 100]; `keepIdx` (the just-edited slot)
 * is tried last so its value stays put. Mirrors the design comp's `roundTo100`.
 */
export function roundSlotsTo100(slots: ScheduleDraft[], keepIdx: number): ScheduleDraft[] {
  const rounded = slots.map((s) => ({ ...s, weight: Math.round(s.weight) }));
  let diff = SLOT_WEIGHT_SUM - rounded.reduce((acc, s) => acc + s.weight, 0);
  for (let i = 0; i < rounded.length && diff !== 0; i++) {
    if (i === keepIdx) continue;
    const w = rounded[i].weight + diff;
    if (w >= 0 && w <= SLOT_WEIGHT_SUM) {
      rounded[i] = { ...rounded[i], weight: w };
      diff = 0;
    }
  }
  if (diff !== 0 && rounded[keepIdx]) {
    rounded[keepIdx] = {
      ...rounded[keepIdx],
      weight: clampNum(rounded[keepIdx].weight + diff, 0, SLOT_WEIGHT_SUM),
    };
  }
  return rounded;
}

/**
 * Set slot `idx` to `weight` and proportionally redistribute the remaining
 * `100 − weight` across the other slots (even split when they're all at 0),
 * then round to an exact sum of 100.
 */
export function rebalanceSlotWeight(
  slots: ScheduleDraft[],
  idx: number,
  weight: number,
): ScheduleDraft[] {
  if (!slots[idx]) return slots;
  const nv = clampNum(Math.round(weight), 0, SLOT_WEIGHT_SUM);
  const remaining = SLOT_WEIGHT_SUM - nv;
  const others = slots.filter((_, i) => i !== idx);
  const sumOthers = others.reduce((acc, o) => acc + o.weight, 0);
  const next = slots.map((s, i) => {
    if (i === idx) return { ...s, weight: nv };
    const w = sumOthers === 0 ? remaining / others.length : (s.weight / sumOthers) * remaining;
    return { ...s, weight: w };
  });
  return roundSlotsTo100(next, idx);
}

/**
 * Split an `[startMin, endMin]` window into TWO ≥{@link PRESET_SLOT_MIN_WIDTH_MIN}-min
 * slots separated by a strictly >{@link SLOT_MIN_GAP_MIN}-min gap, snapped to
 * {@link SPLIT_ROUND_MIN}. The gap targets `gapTarget` (defaults to
 * {@link DISTRIBUTION_SLOT_GAP_MIN}) but is clamped to whatever the window can
 * fit. Returns `null` when the window is too narrow to hold two slots + a
 * >30-min gap — a non-null result is ALWAYS backend-valid (e.g. `09:00→17:00`
 * ⇒ `09:00–12:00` + `14:00–17:00`).
 */
export function splitWindowInTwo(
  startMin: number,
  endMin: number,
  gapTarget: number = DISTRIBUTION_SLOT_GAP_MIN,
): [{ start: number; end: number }, { start: number; end: number }] | null {
  const span = endMin - startMin;
  // Need two ≥SLOT_MIN_DURATION slots plus a strictly >SLOT_MIN_GAP gap.
  if (span < 2 * PRESET_SLOT_MIN_WIDTH_MIN + SLOT_MIN_GAP_MIN + 1) return null;

  const round30 = (x: number) => Math.round(x / SPLIT_ROUND_MIN) * SPLIT_ROUND_MIN;
  const mid = (startMin + endMin) / 2;
  const gap = clampNum(gapTarget, SLOT_MIN_GAP_MIN + 1, span - 2 * PRESET_SLOT_MIN_WIDTH_MIN);

  let leftEnd = round30(mid - gap / 2);
  let rightStart = round30(mid + gap / 2);

  // Restore the invariants after rounding: each slot ≥ SLOT_MIN_DURATION, the
  // boundaries inside the window, and the gap strictly > SLOT_MIN_GAP. Nudge the
  // boundaries outward by one grid step at a time; give up after a bounded number
  // of tries (return null) so a pathological window can't loop.
  for (let guard = 0; guard < span; guard += SPLIT_ROUND_MIN) {
    const leftOk = leftEnd - startMin >= PRESET_SLOT_MIN_WIDTH_MIN;
    const rightOk = endMin - rightStart >= PRESET_SLOT_MIN_WIDTH_MIN;
    const gapOk = rightStart - leftEnd > SLOT_MIN_GAP_MIN;
    const ordered = startMin < leftEnd && leftEnd < rightStart && rightStart < endMin;
    if (leftOk && rightOk && gapOk && ordered) {
      return [
        { start: startMin, end: leftEnd },
        { start: rightStart, end: endMin },
      ];
    }
    // Widen the gap (push the boundaries apart) to satisfy gap/duration, but only
    // while each slot can still keep its minimum duration.
    if (!gapOk || !ordered) {
      if (leftEnd - SPLIT_ROUND_MIN - startMin >= PRESET_SLOT_MIN_WIDTH_MIN) {
        leftEnd -= SPLIT_ROUND_MIN;
      } else if (endMin - (rightStart + SPLIT_ROUND_MIN) >= PRESET_SLOT_MIN_WIDTH_MIN) {
        rightStart += SPLIT_ROUND_MIN;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }
  return null;
}

/** Apply a day-scoped slot transform to the flat schedule, leaving other days as-is. */
function mapDaySlots(
  schedule: ScheduleDraft[],
  dayOfWeek: number,
  fn: (slots: ScheduleDraft[]) => ScheduleDraft[],
): ScheduleDraft[] {
  const byDay = groupByDay(schedule);
  return ungroupByDay(byDay.map((rows, i) => (i === dayOfWeek ? fn(rows) : rows)));
}

/**
 * The slot-aware schedule callbacks `WarmupScheduleSection` raises. Both the
 * single-account hook and the bulk draft hook implement this shape, so the
 * shared `WarmupSettingsForm` takes exactly one schedule-handlers prop.
 */
export type ScheduleHandlers = {
  /** Day-level on/off — flips `enabled` for every slot of the day. */
  onToggleDay: (dayOfWeek: number, enabled: boolean) => void;
  /**
   * Edit one slot's window times by `(day, slotIndex)`. Weight is NOT set here —
   * it follows the section's split rule (`onApplySplitRule`) or, under the
   * custom rule, `onSetSlotWeight`.
   */
  onSlotChange: (
    dayOfWeek: number,
    slotIndex: number,
    patch: Partial<Pick<ScheduleDraft, 'startTime' | 'endTime'>>,
  ) => void;
  /** Split the day's last slot to append a new one (≤ {@link MAX_SLOTS_PER_DAY},
   * and only when the split clears the per-slot/day-window floors — see
   * {@link canAddSlot}); auto-rebalances to 100. */
  onAddSlot: (dayOfWeek: number) => void;
  /** Remove a slot, re-pack indices, and auto-rebalance the rest to 100. */
  onRemoveSlot: (dayOfWeek: number, slotIndex: number) => void;
  /** Custom split only: set one slot's share; the day's other slot absorbs the
   * rest so the day still sums to 100. */
  onSetSlotWeight: (dayOfWeek: number, slotIndex: number, weight: number) => void;
  /** Re-derive every two-slot day's weights from the split rule (`custom`
   * leaves the weights as they are). */
  onApplySplitRule: (rule: SplitRule) => void;
  /** Weekdays / Everyday: flip the days' `enabled` flags, times untouched. */
  onApplyDayMode: (mode: DayMode) => void;
  /** Copy one day's slots onto every other enabled day. */
  onCopyDayToEnabledDays: (dayOfWeek: number) => void;
};

// ── Slot mutations (pure; operate on the flat ScheduleDraft[]) ───────────────────

/**
 * Set `enabled` for every slot of a day. On the OFF→ON transition, re-checks
 * the day against {@link validateSchedule} and repairs it if needed: a
 * weight-only rescale via {@link roundSlotsTo100} when possible, else fall
 * back to {@link defaultSchedule}'s default day.
 */
export function setDayEnabled(
  schedule: ScheduleDraft[],
  dayOfWeek: number,
  enabled: boolean,
): ScheduleDraft[] {
  const next = schedule.map((r) => (r.dayOfWeek === dayOfWeek ? { ...r, enabled } : r));
  if (!enabled) return next;

  const isDayInvalid = (candidate: ScheduleDraft[]): boolean => {
    const validation = validateSchedule(candidate);
    return !validation.ok && validation.errors.some((e) => e.dayOfWeek === dayOfWeek);
  };
  if (!isDayInvalid(next)) return next;

  const rebalanced = mapDaySlots(next, dayOfWeek, (slots) => roundSlotsTo100(slots, 0));
  if (!isDayInvalid(rebalanced)) return rebalanced;

  const defaultDay = defaultSchedule()
    .filter((r) => r.dayOfWeek === dayOfWeek)
    .map((r) => ({ ...r, enabled: true }));
  return mapDaySlots(next, dayOfWeek, () => defaultDay);
}

/** Patch a single slot's fields (window times / weight) by `(day, slotIndex)`. */
export function patchSlot(
  schedule: ScheduleDraft[],
  dayOfWeek: number,
  slotIndex: number,
  patch: Partial<Pick<ScheduleDraft, 'startTime' | 'endTime' | 'weight'>>,
): ScheduleDraft[] {
  return schedule.map((r) =>
    r.dayOfWeek === dayOfWeek && r.slotIndex === slotIndex ? { ...r, ...patch } : r,
  );
}

type SlotSplit = readonly [{ start: number; end: number }, { start: number; end: number }];

// split-last halves the window, so an enabled day under ~7h can't add a
// second slot at all even though the 2-slot ceiling would otherwise allow it
// — MIN_SLOT_DURATION_MIN/MIN_DAILY_WINDOW_MIN bind before MAX_SLOTS_PER_DAY does.
/**
 * Split the day's LAST slot in two, using the minimal legal gap
 * (`SLOT_MIN_GAP_MIN + 1`) so as much of the window as possible stays
 * send-time — not {@link DISTRIBUTION_SLOT_GAP_MIN}, which is calibrated for
 * wide ≥6h windows and eats too
 * much of a narrower day's budget (e.g. a 7h day would fall under the 360min
 * daily floor). Returns `null` when the day has no slots, when the window is
 * too narrow to split at all, or when no split satisfies BOTH floors: each
 * half ≥ {@link MIN_SLOT_DURATION_MIN}, and — for an enabled day — the day's
 * total window (untouched slots + both new halves) stays
 * ≥ {@link MIN_DAILY_WINDOW_MIN}. Shared by {@link addSlot} and
 * {@link canAddSlot} so they can't drift apart.
 */
function splitLastSlot(day: ScheduleDraft[]): SlotSplit | null {
  if (day.length === 0) return null;
  const last = day[day.length - 1];
  const split = splitWindowInTwo(toMinutes(last.startTime), toMinutes(last.endTime), SLOT_MIN_GAP_MIN + 1);
  if (split === null) return null;
  const [a, b] = split;
  const halfA = a.end - a.start;
  const halfB = b.end - b.start;
  if (last.enabled) {
    if (halfA < MIN_SLOT_DURATION_MIN || halfB < MIN_SLOT_DURATION_MIN) return null;
    const otherMinutes = day
      .slice(0, -1)
      .reduce((acc, s) => acc + (toMinutes(s.endTime) - toMinutes(s.startTime)), 0);
    if (otherMinutes + halfA + halfB < MIN_DAILY_WINDOW_MIN) return null;
  }
  return split;
}

/**
 * Whether {@link addSlot} would actually change the day — drives the "Add
 * slot" button's visibility so it simply doesn't render when it would no-op.
 */
export function canAddSlot(day: ScheduleDraft[]): boolean {
  return day.length < MAX_SLOTS_PER_DAY && splitLastSlot(day) !== null;
}

/**
 * Minimum daily window (minutes) before a second slot becomes addable, for a
 * day blocked by the split floors rather than the slot cap; `null` otherwise.
 * Probes the REAL {@link splitLastSlot} — grid rounding breaks a closed form.
 */
export function minWindowMinutesToAddSlot(day: ScheduleDraft[]): number | null {
  if (day.length === 0 || day.length >= MAX_SLOTS_PER_DAY) return null;
  if (splitLastSlot(day) !== null) return null;

  const last = day[day.length - 1];
  const start = toMinutes(last.startTime);
  const otherMinutes = day
    .slice(0, -1)
    .reduce((acc, s) => acc + (toMinutes(s.endTime) - toMinutes(s.startTime)), 0);
  const DAY_END_CAP_MIN = 24 * 60;

  for (let end = toMinutes(last.endTime) + 1; end <= DAY_END_CAP_MIN; end++) {
    const candidate = [...day.slice(0, -1), { ...last, endTime: toHHMM(end) }];
    if (splitLastSlot(candidate) !== null) {
      return otherMinutes + (end - start);
    }
  }
  return null;
}

/** "Xh Ym" rendering of a minute count (420 → "7h", 45 → "45m"). */
export function formatWindowDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * Append a slot to a day by splitting the day's LAST slot in two (see
 * {@link splitLastSlot}), rather than bolting on a fixed window — this always
 * produces a valid, non-overlapping window regardless of the day's current
 * span. No-op past {@link MAX_SLOTS_PER_DAY}, and no-op when the split would
 * violate either the per-slot or per-day floor. The new slot inherits the
 * last slot's `enabled` flag and starts at the next contiguous `slotIndex`.
 * The day is then auto-rebalanced so every slot (including the new one) gets
 * a roughly-even share and the weights still sum to exactly 100.
 */
export function addSlot(schedule: ScheduleDraft[], dayOfWeek: number): ScheduleDraft[] {
  const byDay = groupByDay(schedule);
  const day = byDay[dayOfWeek] ?? [];
  if (day.length >= MAX_SLOTS_PER_DAY) return schedule;
  const split = splitLastSlot(day);
  if (split === null) return schedule;
  const last = day[day.length - 1];
  const [half0, half1] = split;
  const withNew: ScheduleDraft[] = [
    ...day.slice(0, -1),
    { ...last, startTime: toHHMM(half0.start), endTime: toHHMM(half0.end) },
    {
      dayOfWeek,
      slotIndex: day.length,
      weight: SLOT_WEIGHT_MIN,
      enabled: last.enabled,
      startTime: toHHMM(half1.start),
      endTime: toHHMM(half1.end),
    },
  ];
  // Give the new slot an even share and proportionally shrink the others to 100.
  const share = Math.round(SLOT_WEIGHT_SUM / withNew.length);
  const balanced = rebalanceSlotWeight(withNew, withNew.length - 1, share);
  return ungroupByDay(byDay.map((rows, i) => (i === dayOfWeek ? balanced : rows)));
}

/**
 * Remove a slot from a day, re-pack the remaining slots' `slotIndex` to stay
 * contiguous from 0 (preserving window order), and scale the survivors back up
 * to an exact sum of 100. No-op when the day has one slot (a day keeps ≥ 1 row).
 */
export function removeSlot(
  schedule: ScheduleDraft[],
  dayOfWeek: number,
  slotIndex: number,
): ScheduleDraft[] {
  const byDay = groupByDay(schedule);
  const day = byDay[dayOfWeek] ?? [];
  if (day.length <= 1) return schedule;
  const reindexed = day
    .filter((s) => s.slotIndex !== slotIndex)
    .map((s, i) => ({ ...s, slotIndex: i }));
  const sum = reindexed.reduce((acc, s) => acc + s.weight, 0) || 1;
  const scaled = roundSlotsTo100(
    reindexed.map((s) => ({ ...s, weight: (s.weight / sum) * SLOT_WEIGHT_SUM })),
    0,
  );
  return ungroupByDay(byDay.map((rows, i) => (i === dayOfWeek ? scaled : rows)));
}

// ── Schedule validation (full mirror of the backend ScheduleSlotsConstraint) ─────

/** Per-day validation error from {@link validateSchedule}. */
export type ScheduleDayError = { dayOfWeek: number; message: string };

export type ScheduleValidation =
  | { ok: true }
  | { ok: false; errors: ScheduleDayError[] };

/** Minutes-since-midnight for an `HH:MM` string. Exported for the parity tests. */
export function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.slice(0, 5).split(':');
  return Number(h) * 60 + Number(m);
}

/** Zero-padded `HH:MM` for a minutes-since-midnight value. Inverse of {@link toMinutes}. */
export function toHHMM(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Fully mirrors the backend `ScheduleSlotsConstraint` so the form can gate
 * submit with the same semantics the API enforces. Checks run **per enabled
 * day** (disabled days are skipped entirely — never weight-checked):
 *
 *  1. 1–2 slots; `slotIndex` contiguous from 0.
 *  2. `enabled` uniform across the day's slots (caller keeps this invariant, but
 *     a mixed day is still rejected here for parity).
 *  3. Each slot `startTime < endTime`; `weight ≥ 1` integer; each slot starts
 *     strictly >30 min after the previous slot ends.
 *  4. `slotIndex` monotonic with `startTime` (slot 0 earliest … slot 1 latest).
 *  5. Slot weights sum to exactly 100 (enabled days only).
 *  6. An enabled day's slots cumulatively cover ≥ 6h (`InsufficientDailyWindow`
 *     / `022306`).
 *  7. Each slot of an enabled day spans ≥ 2h (`WindowTooShort` / `022302`).
 *
 * Returns structured per-day errors so the UI can surface the first failure
 * inline. Pure + exported so the parity fixture can assert it against the same
 * cases as the backend DTO spec. The backend stays authoritative — this is a
 * UX affordance, not the source of truth.
 */
export function validateSchedule(schedule: ScheduleDraft[]): ScheduleValidation {
  const errors: ScheduleDayError[] = [];
  const byDay = groupByDay(schedule);

  // Rule 0: every day 0-6 must be present with ≥1 slot (mirrors the backend
  // `byDay.size !== 7` / non-empty-array guard). The UI always renders all 7
  // days, but the validator stays a faithful mirror so the parity fixture's
  // "missing a day" / "empty schedule" cases reject here too.
  for (let day = 0; day < 7; day++) {
    if (byDay[day].length === 0) {
      errors.push({ dayOfWeek: day, message: 'Every day of the week must have at least one slot.' });
    }
  }
  if (errors.length > 0) return { ok: false, errors };

  for (let day = 0; day < 7; day++) {
    const slots = byDay[day];
    if (slots.length === 0) continue;

    // Rule 2: enabled uniform across the day. The window/weight/contiguity/
    // monotonic checks below run for EVERY day (mirrors the backend
    // `ScheduleSlotsConstraint.validateDay`, which validates a disabled day's
    // windows too); only the sum-to-100 check (Rule 5) is gated on `enabled`.
    const allEnabled = slots.every((s) => s.enabled);
    const allDisabled = slots.every((s) => !s.enabled);
    if (!allEnabled && !allDisabled) {
      errors.push({ dayOfWeek: day, message: 'All slots in a day must be enabled or disabled together.' });
      continue;
    }

    // Rule 1: 1–2 slots, contiguous slotIndex from 0.
    if (slots.length > MAX_SLOTS_PER_DAY) {
      errors.push({ dayOfWeek: day, message: `A day can have at most ${MAX_SLOTS_PER_DAY} slots.` });
      continue;
    }
    const contiguous = slots.every((s, i) => s.slotIndex === i);
    if (!contiguous) {
      errors.push({ dayOfWeek: day, message: 'Slot positions must be contiguous starting from 0.' });
      continue;
    }

    // Rule 3 + 4: per-slot validity, monotonic windows, >30-min gap. Slots are
    // already sorted by slotIndex (groupByDay), so prevEnd tracks the gap and
    // prevStart tracks startTime-monotonicity.
    let invalidSlot = false;
    let prevEnd = -1;
    let prevStart = -1;
    for (const s of slots) {
      if (!Number.isInteger(s.weight) || s.weight < SLOT_WEIGHT_MIN) {
        errors.push({ dayOfWeek: day, message: `Each slot weight must be a whole number ≥ ${SLOT_WEIGHT_MIN}.` });
        invalidSlot = true;
        break;
      }
      const start = toMinutes(s.startTime);
      const end = toMinutes(s.endTime);
      if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end) {
        errors.push({ dayOfWeek: day, message: 'Each slot must start before it ends.' });
        invalidSlot = true;
        break;
      }
      // Rule 7: each slot of an ENABLED day must span ≥ 2h — the server's
      // per-slot send-window floor (`validateSendWindow` / `MIN_SEND_WINDOW_HOURS`,
      // mirrored by the DTO's `ScheduleSlotsConstraint`), which throws
      // `WindowTooShort` / `022302`. Disabled days are exempt (the backend gates
      // this on `if (enabled)`). Slot-local, so it fires here in the per-slot
      // loop — right after start<end establishes a positive duration.
      if (allEnabled && end - start < MIN_SLOT_DURATION_MIN) {
        errors.push({ dayOfWeek: day, message: 'Each sending slot must be at least 2 hours.' });
        invalidSlot = true;
        break;
      }
      // Rule 4: slotIndex monotonic with startTime.
      if (start <= prevStart) {
        errors.push({ dayOfWeek: day, message: 'Later slots must start after earlier ones.' });
        invalidSlot = true;
        break;
      }
      // Rule 3: each slot must start STRICTLY MORE than 30 min after the
      // previous slot ends (same `SLOT_MIN_GAP_MIN` floor the backend enforces).
      if (prevEnd >= 0 && start - prevEnd <= SLOT_MIN_GAP_MIN) {
        errors.push({ dayOfWeek: day, message: 'Each slot must start more than 30 minutes after the previous slot ends.' });
        invalidSlot = true;
        break;
      }
      prevStart = start;
      prevEnd = end;
    }
    if (invalidSlot) continue;

    // Rule 5: an ENABLED day's weights sum to exactly 100 (a disabled day is
    // not weight-summed — matches the backend's `if (enabled)` gate).
    if (allEnabled && daySlotWeightSum(slots) !== SLOT_WEIGHT_SUM) {
      errors.push({ dayOfWeek: day, message: `Slot weights for the day must sum to ${SLOT_WEIGHT_SUM}.` });
    }

    // Rule 6: an ENABLED day's slots must cumulatively cover ≥ 6h (mirrors the
    // backend `InsufficientDailyWindow` / `022306` rule). Disabled days are
    // exempt — they send nothing. Exactly 6h passes; < 6h is rejected.
    if (allEnabled && daySlotMinutes(slots) < MIN_DAILY_WINDOW_MIN) {
      errors.push({
        dayOfWeek: day,
        message: 'Each active day needs at least 6 hours of total sending window.',
      });
    }
  }

  return errors.length === 0 ? { ok: true } : { ok: false, errors };
}

// Canonical supported-languages set — mirrors the `WarmupLanguage` enum on the
// API (`@trulyinbox/types`). Re-saving an account stored on a now-dropped
// language (Swedish/Norwegian/Danish/Finnish/Polish) will fail @IsIn on the
// backend; the user must pick one of these to re-save.
export const LANGUAGE_OPTIONS = [
  'English', 'Spanish', 'French', 'German', 'Portuguese', 'Chinese',
  'Japanese', 'Arabic', 'Hindi', 'Russian', 'Italian', 'Dutch',
];

// Mirrors `EmailAccountWarmupConfig.language`'s column default
// (`packages/database/src/entities/email-account-warmup-config.entity.ts`) —
// what "Reset to default" in the Email content section restores.
export const DEFAULT_WARMUP_LANGUAGE = 'English';

// ── Industry & topics ─────────────────────────────────────────────────────────

// Predefined catalogs shared with the backend (`@trulyinbox/types`): the FE
// renders them as dropdowns and the API enforces the same lists via `@IsIn`, so
// the two never drift. We expose just the display `value` strings here — that's
// what's persisted and fed to the AI content pipeline.
export const INDUSTRY_OPTIONS = TARGET_INDUSTRY_VALUES;
export const TOPIC_OPTIONS = SUBTOPIC_VALUES;

/** Max topics an `industry_topic` account may carry (mirrors the DTO's `@ArrayMaxSize(5)`). */
export const MAX_TOPICS = 5;

/**
 * Dropdown options for the industry Select, always including the currently
 * stored value even if it predates the curated list (older accounts saved a
 * free-text industry). Without this the Radix Select trigger renders blank for
 * any stored value not in {@link INDUSTRY_OPTIONS}. Mirrors
 * {@link timezoneOptionsForValue}.
 */
export function industryOptionsForValue(current: string | null | undefined): string[] {
  const trimmed = current?.trim();
  if (!trimmed) return [...INDUSTRY_OPTIONS];
  if (INDUSTRY_OPTIONS.includes(trimmed)) return [...INDUSTRY_OPTIONS];
  return [trimmed, ...INDUSTRY_OPTIONS];
}

/**
 * Topics still available to add: the predefined catalog minus what's already
 * selected. The picker is "add only" (selecting adds a chip), so already-chosen
 * topics are filtered out rather than shown as checked. Legacy stored topics not
 * in the catalog still render as removable chips — they just can't be re-added.
 */
export function availableTopicOptions(selected: string[]): string[] {
  const chosen = new Set(selected);
  return TOPIC_OPTIONS.filter((t) => !chosen.has(t));
}

// ── Email content mode ──────────────────────────────────────────────────────

// Mirrors `EmailAccountWarmupConfig.contentMode`'s column default (same
// entity as `DEFAULT_WARMUP_LANGUAGE` above) — what "Reset to default" in the
// Email content section restores, alongside clearing industry/topics/customPrompt.
export const DEFAULT_CONTENT_MODE: ContentMode = 'default';

/**
 * Derive the API `contentMode` from the Industry & Topic fields. The frontend
 * no longer offers a mode picker — `contentMode` isn't a user choice, it's
 * computed from whether the pair is filled in: both present → `industry_topic`;
 * either empty → `default`, which already renders generic warmup content
 * server-side today, unchanged. `custom_prompt` is never derived — the FE
 * doesn't offer that mode any more (the backend still accepts it for any
 * account saved onto it by another channel).
 */
export function deriveContentMode(industry: string, topics: readonly string[]): ContentMode {
  return industry.trim() && topics.length > 0 ? 'industry_topic' : 'default';
}

export type ContentValidation = { ok: true } | { ok: false; message: string };

/**
 * Client-side mirror of the backend cross-field validator, restated for the
 * derived `contentMode` (see {@link deriveContentMode}): Industry and Topics
 * must be filled in TOGETHER or not at all — one filled and the other blank
 * is the only invalid state (it can't resolve to either `default` or
 * `industry_topic`). Both empty is valid (→ `default`); both filled is valid
 * (→ `industry_topic`).
 */
export function validateContent(draft: WarmupDraft): ContentValidation {
  const hasIndustry = !!draft.industry.trim();
  const hasTopics = draft.topics.length > 0;
  if (hasIndustry && !hasTopics) {
    return { ok: false, message: 'Add at least one topic for Industry & Topic content.' };
  }
  if (!hasIndustry && hasTopics) {
    return { ok: false, message: 'Enter an industry for Industry & Topic content.' };
  }
  return { ok: true };
}

// Full IANA timezone list from the platform's Intl database. Computed once at
// module load; the list is alphabetical and includes UTC. If the runtime
// doesn't expose `Intl.supportedValuesOf` (very old browsers), we fall back to
// a small curated list so the dropdown still works.
const PLATFORM_TIMEZONES: readonly string[] =
  typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function'
    ? Intl.supportedValuesOf('timeZone')
    : [
        'UTC',
        'America/New_York',
        'America/Chicago',
        'America/Denver',
        'America/Los_Angeles',
        'Europe/London',
        'Europe/Paris',
        'Europe/Berlin',
        'Asia/Kolkata',
        'Asia/Singapore',
        'Asia/Tokyo',
        'Australia/Sydney',
      ];

// Some Intl implementations omit the plain `UTC` alias and only list canonical
// zones — when the API stores `'UTC'`, Radix Select would render the trigger
// blank because no `<SelectItem value="UTC">` matches. Guarantee UTC is
// present and keep the list alphabetical.
export const TIMEZONE_OPTIONS: string[] = Array.from(
  new Set<string>(['UTC', ...PLATFORM_TIMEZONES]),
).sort((a, b) => a.localeCompare(b));

/**
 * Builds the dropdown's option list, always including the currently-stored
 * `timezone` value even if the runtime's Intl database doesn't know it
 * (legacy values like `EST` saved by older clients, or future-canonical zones
 * the browser hasn't picked up yet). Without this, the Select trigger renders
 * empty for any value not in `TIMEZONE_OPTIONS`.
 */
export function timezoneOptionsForValue(current: string | null | undefined): string[] {
  if (!current) return TIMEZONE_OPTIONS;
  if (TIMEZONE_OPTIONS.includes(current)) return TIMEZONE_OPTIONS;
  return [current, ...TIMEZONE_OPTIONS];
}

// The product-wide ceiling on reply rate (%). Re-exported so the form has one
// feature-local import site and the number never drifts from the backend.
export { REPLY_RATE_ABSOLUTE_CAP };

export const ESP_PCT_MIN = 0;
export const ESP_PCT_MAX = 100;
export const ESP_PCT_SUM = 100;

// The custom-split BAR is stricter than the stored-value bounds above: a
// provider gets between 10% and 60%, in steps of 10. `ESP_PCT_MIN`/`MAX` stay
// at the API's own 0–100 for validation, so a mailbox saved with an older,
// finer split is still saveable — the bar just moves it onto the grid the
// moment a handle is touched.
export const ESP_SPLIT_MIN = 10;
export const ESP_SPLIT_MAX = 60;
export const ESP_SPLIT_STEP = 10;

/**
 * Inline style for a `.range-slider` input (the Templates section's ratio
 * slider — see `WarmupTemplateCollectionSection.tsx`). Paints the filled portion with
 * `accent` and leaves the remainder on the themed `--ink-200` track (so the
 * unfilled background tracks light/dark mode). `accent` is any CSS colour —
 * a token like `var(--brand-500)` or a raw value. The fill percentage is
 * derived from value/min/max and clamped to [0, 100].
 */
export function rangeStyle(
  accent: string,
  value: number,
  min: number,
  max: number,
): CSSProperties {
  const span = max - min;
  const pct = span > 0 ? Math.min(100, Math.max(0, ((value - min) / span) * 100)) : 0;
  return {
    '--range-accent': accent,
    '--range-track': `linear-gradient(to right, ${accent} ${pct}%, var(--ink-200) ${pct}%)`,
  } as CSSProperties;
}

export type EspKey = 'espGooglePct' | 'espMicrosoftPct' | 'espOthersPct';

export type EspBucketMeta = {
  key: EspKey;
  label: string;
  color: string;
};

export const ESP_BUCKETS: readonly EspBucketMeta[] = [
  // Provider brand hues (the tokens-only carve-out lives in `ProviderIcon.tsx`);
  // the section renders them as soft tints, never at full strength.
  { key: 'espGooglePct', label: 'Google', color: PROVIDER_BRAND_COLOR.googleRed },
  { key: 'espMicrosoftPct', label: 'Microsoft', color: PROVIDER_BRAND_COLOR.microsoft },
  { key: 'espOthersPct', label: 'Others', color: 'var(--ink-400)' },
] as const;

// ── ESP helpers ──────────────────────────────────────────────────────────────

export type EspValidation = { ok: true } | { ok: false; message: string };

export function validateEspBuckets(draft: WarmupDraft): EspValidation {
  if (draft.espMode !== 'CUSTOM') return { ok: true };
  const all = [draft.espGooglePct, draft.espMicrosoftPct, draft.espOthersPct];
  if (all.some((v) => !Number.isFinite(v))) {
    return { ok: false, message: 'Enter a number for Google %, Microsoft %, and Others %.' };
  }
  if (all.some((v) => v < ESP_PCT_MIN || v > ESP_PCT_MAX)) {
    return {
      ok: false,
      message: `Each ESP must be between ${ESP_PCT_MIN}% and ${ESP_PCT_MAX}%.`,
    };
  }
  if (draft.espGooglePct + draft.espMicrosoftPct + draft.espOthersPct !== ESP_PCT_SUM) {
    return { ok: false, message: `Google %, Microsoft %, and Others % must sum to ${ESP_PCT_SUM}.` };
  }
  return { ok: true };
}

/**
 * Linked-slider rebalancer. When a user drags one ESP to `newValue`, the
 * remaining 100 − newValue is split between the other two ESPs in proportion
 * to their CURRENT values (so a 60/30/10 distribution stays roughly the
 * same shape when the dragged one moves a little). When both siblings are
 * at 0, the remaining is split evenly. The result is rounded so the three
 * values are integers and still sum to exactly 100.
 */
export function rebalanceEspBuckets(
  draft: WarmupDraft,
  changed: EspKey,
  newValueRaw: number,
): Pick<WarmupDraft, 'espGooglePct' | 'espMicrosoftPct' | 'espOthersPct'> {
  const newValue = Math.min(ESP_PCT_MAX, Math.max(ESP_PCT_MIN, Math.round(newValueRaw)));
  const siblings = ESP_BUCKETS
    .map((b) => b.key)
    .filter((k) => k !== changed) as [EspKey, EspKey];
  const [s1, s2] = siblings;
  const oldS1 = draft[s1];
  const oldS2 = draft[s2];
  const remaining = ESP_PCT_SUM - newValue;
  const siblingSum = oldS1 + oldS2;

  let v1: number;
  let v2: number;
  if (siblingSum === 0) {
    v1 = Math.floor(remaining / 2);
    v2 = remaining - v1;
  } else {
    v1 = Math.round((oldS1 / siblingSum) * remaining);
    v2 = remaining - v1;
  }

  // Clamp + redistribute any overshoot. After clamping one sibling to [0, MAX]
  // the other absorbs the diff to keep the sum at remaining.
  if (v1 < 0) { v2 += v1; v1 = 0; }
  if (v1 > ESP_PCT_MAX) { v2 -= (v1 - ESP_PCT_MAX); v1 = ESP_PCT_MAX; }
  if (v2 < 0) { v1 += v2; v2 = 0; }
  if (v2 > ESP_PCT_MAX) { v1 -= (v2 - ESP_PCT_MAX); v2 = ESP_PCT_MAX; }

  return {
    espGooglePct: changed === 'espGooglePct' ? newValue : ({ [s1]: v1, [s2]: v2 } as Record<EspKey, number>).espGooglePct,
    espMicrosoftPct: changed === 'espMicrosoftPct' ? newValue : ({ [s1]: v1, [s2]: v2 } as Record<EspKey, number>).espMicrosoftPct,
    espOthersPct: changed === 'espOthersPct' ? newValue : ({ [s1]: v1, [s2]: v2 } as Record<EspKey, number>).espOthersPct,
  };
}

/**
 * Shift weight across the boundary between ESP bucket `boundaryIdx` and the next,
 * holding their combined share constant: the left bucket becomes `leftWeight`
 * snapped to {@link ESP_SPLIT_STEP} and clamped so BOTH sides stay within
 * [{@link ESP_SPLIT_MIN}, {@link ESP_SPLIT_MAX}], and the right bucket absorbs
 * the rest. The third bucket is untouched, so the three still sum to 100.
 * Drives the ESP split bar.
 *
 * {@link espBoundaryRange} is the same clamp, exposed for the handle's
 * `aria-valuemin`/`aria-valuemax`. A pair whose total can't satisfy the bounds
 * on both sides (only reachable from stored legacy values) is left as it is.
 */
export function espBoundaryRange(
  draft: WarmupDraft,
  boundaryIdx: number,
): { min: number; max: number } | null {
  const leftKey = ESP_BUCKETS[boundaryIdx]?.key;
  const rightKey = ESP_BUCKETS[boundaryIdx + 1]?.key;
  if (!leftKey || !rightKey) return null;
  const pairTotal = draft[leftKey] + draft[rightKey];
  const min = Math.max(ESP_SPLIT_MIN, pairTotal - ESP_SPLIT_MAX);
  const max = Math.min(ESP_SPLIT_MAX, pairTotal - ESP_SPLIT_MIN);
  return min <= max ? { min, max } : null;
}

export function shiftEspBoundary(
  draft: WarmupDraft,
  boundaryIdx: number,
  leftWeight: number,
): Pick<WarmupDraft, 'espGooglePct' | 'espMicrosoftPct' | 'espOthersPct'> {
  const base = {
    espGooglePct: draft.espGooglePct,
    espMicrosoftPct: draft.espMicrosoftPct,
    espOthersPct: draft.espOthersPct,
  };
  const leftKey = ESP_BUCKETS[boundaryIdx]?.key;
  const rightKey = ESP_BUCKETS[boundaryIdx + 1]?.key;
  if (!leftKey || !rightKey) return base;

  const range = espBoundaryRange(draft, boundaryIdx);
  if (!range) return base;

  const pairTotal = draft[leftKey] + draft[rightKey];
  const snapped = Math.round(leftWeight / ESP_SPLIT_STEP) * ESP_SPLIT_STEP;
  const nextLeft = clampNum(snapped, range.min, range.max);
  base[leftKey] = nextLeft;
  base[rightKey] = pairTotal - nextLeft;
  return base;
}
